import React from 'react';
import FieldConfiguration, {TranslatedWord} from './FieldConfiguration';
import {IFieldSkeleton} from './IFieldSkeleton';
import {SectionSkeleton} from './SectionSkeleton';
import {LanguageId} from '../utils/Languages';
import {FieldView} from './FieldView';
import {SectionView} from './SectionView';
import ElementConfiguration from './ElementConfiguration';
import {SectionConfigurationQL} from '../graphql/SectionConfigurationQL';

export default class SectionConfiguration extends ElementConfiguration {
  protected elementName = 'section'

  static fromQL(data: SectionConfigurationQL): SectionConfiguration {
    const fields = data.fields.map((x) => new FieldConfiguration(x));
    return new SectionConfiguration(data.id, data.labels, fields, data.sortableFields, data.editable);
  }

  constructor(
    readonly id: string,
    readonly labels: TranslatedWord[],
    readonly fields: FieldConfiguration[],
    readonly sortableFields: boolean,
    readonly editable: boolean,
  ) {
    super();
  }

  getLabel(language: LanguageId): string {
    return this.labels.find((x) => x.language === language)?.translation || '';
  }

  replaceFields(fields: FieldConfiguration[]): SectionConfiguration {
    return new SectionConfiguration(this.id, this.labels, fields, this.sortableFields, this.editable);
  }

  /**
   * Remove fields from the section
   * @param ids Ids of the fields
   */
  removeFields(ids: string[]): SectionConfiguration {
    const fields = this.fields.filter((x) => ids.indexOf(x.id) < 0);
    return this.replaceFields(fields);
  }

  toSkeleton(): SectionSkeleton {
    const fields: IFieldSkeleton[] = this.fields.map((x) => {
      const field: IFieldSkeleton = {id: x.id, required: x.required};
      return field;
    });
    return {
      id: this.id,
      fields,
    };
  }

  toView(activeLanguage: LanguageId, defaultLanguage: LanguageId, fields: FieldView[] = []): SectionView {
    const sortableName = this.getLabel(activeLanguage);
    const haveTranslations = this.labels.filter((x) => x.translation.length > 0).map((x) => x.language);
    const translationRequired = haveTranslations.indexOf(defaultLanguage) < 0;
    const label: React.ReactNode = translationRequired ?
      this.getTranslationNotice(sortableName, activeLanguage, defaultLanguage) :
      sortableName;
    return {
      id: this.id,
      name: label,
      sortableName,
      sortableFields: this.sortableFields,
      fields: fields,
      editable: this.editable,
      translationRequired: translationRequired || fields.find((x) => x.translationRequired) !== undefined,
      highlightError: translationRequired && activeLanguage === defaultLanguage,
    };
  }
}
